import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  getCurrentUser,
  fetchUserAttributes,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResend,
  signOut as amplifySignOut,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmReset,
  confirmSignIn as amplifyConfirmSignIn,
} from 'aws-amplify/auth'
import { publicFetch } from '../api/client'

export interface AuthUserInfo {
  userId: string
  email: string
}

interface AuthValue {
  user: AuthUserInfo | null
  loading: boolean
  refresh: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, turnstileToken?: string) => Promise<{ needsConfirm: boolean }>
  confirmSignUp: (email: string, code: string) => Promise<void>
  resendCode: (email: string) => Promise<void>
  signOut: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>
  /** Passwordless: ask for a sign-in link by email. */
  requestMagicLink: (email: string) => Promise<void>
  /** Passwordless: complete sign-in with the token from that link. */
  completeMagicLink: (email: string, token: string) => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUserInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const current = await getCurrentUser()
      let email = current.signInDetails?.loginId || ''
      if (!email) {
        const attrs = await fetchUserAttributes().catch(() => ({}) as Record<string, string>)
        email = (attrs as Record<string, string>).email || ''
      }
      setUser({ userId: current.userId, email })
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value: AuthValue = {
    user,
    loading,
    refresh,
    requestMagicLink: async (email) => {
      // Cognito only runs the custom-auth triggers for a user that already
      // exists, so ensure one first — otherwise a brand-new person silently
      // gets a dummy challenge and no link. Always 200s; never reveals whether
      // the address was already registered.
      await publicFetch('/api/public/magic-start', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }).catch(() => {}) // provisioning is best-effort; the challenge still runs
      await amplifySignIn({ username: email, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } })
    },
    completeMagicLink: async (email, token) => {
      await amplifySignIn({ username: email, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } })
      await amplifyConfirmSignIn({ challengeResponse: token })
      await refresh()
    },
    signIn: async (email, password) => {
      await amplifySignIn({ username: email, password })
      await refresh()
    },
    signUp: async (email, password, turnstileToken) => {
      const res = await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
          ...(turnstileToken ? { clientMetadata: { turnstileToken } } : {}),
        },
      })
      return { needsConfirm: !res.isSignUpComplete }
    },
    confirmSignUp: async (email, code) => {
      await amplifyConfirmSignUp({ username: email, confirmationCode: code })
    },
    resendCode: async (email) => {
      await amplifyResend({ username: email })
    },
    signOut: async () => {
      await amplifySignOut()
      setUser(null)
    },
    forgotPassword: async (email) => {
      await amplifyResetPassword({ username: email })
    },
    confirmForgotPassword: async (email, code, newPassword) => {
      await amplifyConfirmReset({ username: email, confirmationCode: code, newPassword })
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
